import { AvailabilityRequest, Job, Properties, Property, Reservation, ReservationGuest, ReservationState } from './../../models/schedule-weekly-view.model';
import { Injectable } from '@angular/core';
import { DateTime, DateTime as LuxonDateTime } from 'luxon';

import { Aws } from 'src/providers/aws/aws';
import { Client } from 'src/providers/client/client';
import { HttpClientCustom } from 'src/providers/custom/http-client';
import { FilePicker } from 'src/shared/providers/file-picker/file-picker';
import { WindowService } from 'src/shared/providers/window.service';

import { JobCard, SelectedFilters } from 'src/models/schedule.model';
import { channelBreakdownView, reservationCurrency, reservationPriceCents } from 'src/models/guest-reservations';

import { generateUuid } from 'src/providers/tidy-session/session-uuid';
import { AddressModel } from 'src/models/address.model';
import { Util } from 'src/shared/util/util';
import { ReportIssue } from '../report-issue/report-issue';
import { BehaviorSubject } from 'rxjs';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

// The backend spells our own channel 'Tidy' (source_type_name), but the brand
// is written TIDY everywhere it's shown — e.g. the schedule popover's
// "TIDY inquiry" subtitle. Other channels keep the name the API sends.
function normalizeSourceName(name?: string | null): string | undefined {
  if (!name) {
    return name ?? undefined;
  }
  return name.trim().toLowerCase() === 'tidy' ? 'TIDY' : name;
}

@Injectable()
export class Schedule {

  updateIcons = {
    warning: 'assets/svg/alert-circle.svg',
    confirmation: 'assets/svg/checkmark-circle.svg',
    searching: 'assets/svg/search-circle.svg',
  };
  reloadSchedulePage$ = new BehaviorSubject<boolean>(false);
  properties: Properties = [];
  imutableProperties: Properties = [];
  weeklyViewContainer: any;
  weeklyViewScrollPosition: any;

  categoriesImages = {
    regular_cleaning: 'assets/img/service-categories/regular_cleaning.svg',
    pool_cleaning: 'assets/img/service-categories/pool_cleaning.svg',
    carpet_cleaning: 'assets/img/service-categories/carpet_cleaning.svg',
    window_washing: 'assets/img/service-categories/window_washing.svg',
    lawn_garden_care: 'assets/img/service-categories/lawn_garden_care.svg',
    junk_removal: 'assets/img/service-categories/junk_removal.svg',
    pest_control: 'assets/img/service-categories/pest_control.svg',
    pressure_washing: 'assets/img/service-categories/pressure_washing.svg',
    tree_trimming: 'assets/img/service-categories/tree_trimming.svg',
    inspection: 'assets/img/service-categories/inspection.svg',
    trash_service: 'assets/img/service-categories/trash_service.svg',
    handyman: 'assets/img/service-categories/handyman.svg',
    laundry: 'assets/img/service-categories/laundry.svg',
    locksmith: 'assets/img/service-categories/locksmith.svg',
    painting: 'assets/img/service-categories/painting.svg',
    grill_maintenance: 'assets/img/service-categories/grill_maintenance.svg',
    biohazard_cleaning: 'assets/img/service-categories/biohazard_cleaning.svg',
    plumbing: 'assets/img/service-categories/plumbing.svg',
    appliance_repair: 'assets/img/service-categories/appliance_repair.svg',
  };

  constructor(
    private aws: Aws,
    private client: Client,
    private httpClient: HttpClientCustom,
    private filePicker: FilePicker,
    public windowService: WindowService,
    private util: Util,
    private reportIssue: ReportIssue
  ) {}

  getCards(addressId) {
    const url = `api/v1/customer/addresses/${addressId}/cards/list?response_format=minimum`;
    return this.httpClient.get(url);
  }

  getFullCards(addressId) {
    const url = `api/v1/customer/addresses/${addressId}/cards/list?response_format=full`;
    return this.httpClient.get(url);
  }

  async getJobs(addressId) {
    // Use calendar-view endpoint which is optimized and includes all necessary job data
    // Add date range limits: 7 days in the past, 30 days in the future
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 7);
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 30);

    // Format dates as YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const url = `api/v1/customer/calendar-view?scope=jobs&calendar_start_date=${formatDate(startDate)}&calendar_end_date=${formatDate(endDate)}&filters[address_ids]=${addressId}`;
    const response = await this.httpClient.get(url, {}, 20000, true);
    // Calendar view returns array of date objects with jobs array, flatten to get all jobs
    const allJobs = [];
    if (response && Array.isArray(response)) {
      response.forEach(dateObj => {
        if (dateObj.jobs && Array.isArray(dateObj.jobs)) {
          allJobs.push(...dateObj.jobs);
        }
      });
    }
    return allJobs;
  }

  getJobCardsMinimum(addressId, jobIds) {
    const url = `api/v1/customer/addresses/${addressId}/cards/list?job_ids=${jobIds.join(',')}&response_format=minimum`;
    return this.httpClient.get(url);
  }

  getJobCards(addressId, jobIds) {
    const url = `api/v1/customer/addresses/${addressId}/cards/list?job_ids=${jobIds.join(',')}&response_format=full`;
    return this.httpClient.get(url);
  }

  async getJobCard(addressId, jobId): Promise<JobCard> {
    const response = await this.getJobCards(addressId, [jobId]);
    if (response?.[0]?.template === 'pending_coops') {
      return response[1];
    } else {
      return response[0];
    }
  }

  // A job with a pending substitute request also gets a `pending_coops-<jobId>` card listed first,
  // which carries no job or booking data.
  findJobCard(cards: JobCard[], jobId: number): JobCard | null {
    return cards?.find(card => card.template !== 'pending_coops' && card.id?.endsWith(`-${jobId}`)) || null;
  }

  async getJobDetail(cleaningId): Promise<any> {
    const url = `api/v1/me/jobs/cards/${cleaningId}?includes=job_durations`;
    return await this.httpClient.get(url);
  }

  async getJobDetailWithPayment(cleaningId): Promise<any> {
    const url = `api/v1/me/jobs/cards/${cleaningId}?includes=job_durations,payment`;
    return await this.httpClient.get(url);
  }

  async getJobEvents(bookingId: number): Promise<any> {
    const url = `api/v1/customer/customer-data-events?booking_id=${bookingId}`;
    return await this.httpClient.get(url);
  }

  async getUpdates(cleaningUpdateId) {
    const url = `api/v1/customer/cleaning-updates/${cleaningUpdateId}/page`;
    const icons = {
      skip: 'close',
      keep: 'contact',
      reschedule: 'play-forward',
      request: 'play-forward',
      loved_it: 'tidy-love-it',
      phone_call: 'call',
      phone_text: 'text',
      cancel: 'close',
      needs_work: 'tidy-needs-work',
    };
    const result = await this.httpClient.get(url);
    result.buttons = result.buttons.map((item) => {
      item.formatedIcon = icons[item.icon];
      return item;
    });
    return result;
  }

  cancelJob(bookingId) {
    const url = `api/v1/customer/bookings/${bookingId}/cancel`;
    return this.httpClient.post(url, {});
  }

  cancelPlan(planId) {
    const url = `api/v1/customer/plans/${planId}/cancel`;
    return this.httpClient.post(url, {});
  }

  cancelCrafRequest(availabilityRequestId: string) {
    const addressId = parseInt(localStorage.getItem('addressId'));
    const data = {
      availability_request_id: availabilityRequestId,
    };
    const url = `api/v1/customer/addresses/${addressId}/availability-requests/${availabilityRequestId}`;
    return this.httpClient.delete(url, data);
  }

  parseJobs(scheduleCards) {
    const jobs = [];
    scheduleCards.map((card) => {
      //TODO Add handling for other service types
      if (
        card.template === 'cleaning' ||
        card.template === 'cleaning_options_only'
      ) {
        jobs.push(card);
      }
    });
    return jobs;
  }

  getContactData(cardId, addressId = parseInt(localStorage.getItem('addressId'))) {
    const url = `api/v1/customer/addresses/${addressId}/cards/${cardId}/contact`;
    return this.httpClient.get(url);
  }

  createJobLink(payload) {
    const url = 'api/v1/customer/shared-links';
    return this.httpClient.post(url, payload);
  }

  didNotRequestSubstitute(scenario): boolean {
    const dontShowSenarios = [
      'same_day_cleaning_substitute_requested',
      'same_day_cleaning_substitute_failed',
    ];
    if (dontShowSenarios?.includes(scenario)) {
      return false;
    }
    return true;
  }

  showToDoButton(scenario): boolean {
    const dontShowSenarios = [
      'hk_confirmed_no_access',
      'hk_reported_client_cancellation',
      'same_day_cleaning_substitute_requested',
      'same_day_cleaning_substitute_failed',
      null,
      undefined
    ];
    if (dontShowSenarios?.includes(scenario)) {
      return false;
    }
    return true;
  }

  showRescheduleButton(scenario): boolean {
    const dontShowSenarios = [
      'hk_confirmed_no_access',
      'hk_reported_client_cancellation',
      'same_day_cleaning_substitute_requested',
      'same_day_cleaning_substitute_failed',
      'customer_reported_hk_no_showed',
      'unassigned_notification_sent',
      'hk_initiated_no_access',
    ];
    if (dontShowSenarios?.includes(scenario)) {
      return false;
    }
    return true;
  }

  showBackupTimesButton(scenario): boolean {
    const dontShowSenarios = [
      'hk_confirmed_no_access',
      'hk_reported_client_cancellation',
      'same_day_cleaning_substitute_requested',
      'same_day_cleaning_substitute_failed',
    ];
    if (dontShowSenarios?.includes(scenario)) {
      return false;
    }
    return true;
  }

  showCallText(proDisplayName, isPrivateJob): boolean {
    const backendReturnedString = proDisplayName !== null;
    const stringIsAssignedPro = !/Available|Found/.test(proDisplayName);
    return backendReturnedString && stringIsAssignedPro && !isPrivateJob;
  }

  callToActionText(callToAction) {
    if (callToAction === 'Optional Actions (No Action Needed)') {
      return 'Optional Actions';
    } else {
      return callToAction;
    }
  }

  showNotificationHistory(history) {
    if (history) {
      const hasHistory =
        history.length > 0 &&
        history[0]?.text !==
          'We are starting to contact pros in order of your priority list. Please check back soon for a full history.';
      const hasOnlyTidyBestAvailable =
        (history.length === 2 && history[0]?.text === 'Next: waiting for pros' && history[1].text === 'Contacting new pros') ||
        (history.length === 1 && history[0].text === 'New Pros');
      return hasHistory && !hasOnlyTidyBestAvailable;
    } else {
      return false;
    }
  }

  markJobComplete(payload, bookingId) {
    const url = `api/v1/customer/bookings/${bookingId}/complete`;
    return this.httpClient.post(url, payload);
  }

  getCategoryImage(serviceKey) {
    if (!serviceKey) {
      return 'assets/img/service-categories/regular_cleaning.svg';
    }
    if (serviceKey?.includes('.')) {
      const array = serviceKey?.split('.');
      serviceKey = array?.length === 2 ? array?.[0] : array?.[1];
    }
    return this.categoriesImages?.[serviceKey];
  }

  async addAttachment() {
    try {
      const uuid = generateUuid();
      const file = await this.filePicker.pickFile();
      const fileKey = `message/${uuid}/${file.filename}`;
      if (file.format.includes('jpeg') || file.format.includes('png')) {
        const response = await this.aws.uploadFileToS3(
          file.dataUrl,
          fileKey,
          file.format
        );
        return { file, location: response.Location };
      } else {
        return {
          file: null,
          location: '',
        }
      }
    } catch (_error) {
      return {
        file: null,
        location: '',
      }
    }
  }

  keepOneProForTwoProJob(jobId) {
    const url = `api/v1/customer/cleanings/${jobId}/mark-half-cleaning-as-ok`;
    return this.httpClient.post(url, {});
  }

  getScheduleCalendarAllDays() {
    const url = 'api/v1/customer/calendar-view?includes=service_category,guests';
    return this.httpClient.get(url);
  }

  getScheduleCalendar(startDate, endDate, filters, scope?) {
    const searchParams = new URLSearchParams();
    searchParams.append('calendar_start_date', startDate);
    searchParams.append('calendar_end_date', endDate);
    if (scope) {
      searchParams.append('scope', scope);
    }
    if (filters.homekeeper_ids && filters.homekeeper_ids.length > 0) {
      searchParams.append('filters[homekeeper_ids]', filters.homekeeper_ids.join(','));
    }
    if (filters.address_ids && filters.address_ids.length > 0) {
      searchParams.append('filters[address_ids]', filters.address_ids.join(','));
    }
    const url = `api/v1/customer/calendar-view?${searchParams.toString()}&includes=service_category,guests`;
    return this.httpClient.get(url, {}, 20000, true);
  }

  getBookingNotesForJob(bookingId) {
    const url = `api/v1/customer/booking-notes?booking_id=${bookingId}`;
    return this.httpClient.get(url);
  }

  createJobNote(payload) {
    const url = `api/v1/customer/booking-notes`;
    return this.httpClient.post(url, payload);
  }

  updateJobNote(bookingNoteId, payload) {
    const url = `api/v1/customer/booking-notes/${bookingNoteId}`;
    return this.httpClient.put(url, payload);
  }

  deleteJobNote(bookingNoteId) {
    const url = `api/v1/customer/booking-notes/${bookingNoteId}`;
    return this.httpClient.delete(url);
  }

  getMomentImage(momentType) {
    const momentStyleArray = {
      hk_on_the_way: 'assets/img/map-black-car.svg',
      hk_arrived_at_home: 'assets/img/map-light-green-check.svg',
      hk_started_cleaning: 'assets/img/map-green-check.svg',
      hk_initiated_cant_find_home: 'assets/img/map-red-alert.svg',
      hk_found_home: 'assets/img/map-happy-face.svg',
      hk_confirmed_cant_find_home: 'assets/img/map-red-alert.svg',
      hk_initiated_no_access: 'assets/img/map-red-alert.svg',
      hk_got_access: 'assets/img/map-happy-face.svg',
      hk_confirmed_no_access: 'assets/img/map-red-alert.svg',
      hk_reported_client_cancellation: 'assets/img/map-red-alert.svg',
      'same_day_cleaning_substitute_requested.svg': null,
      same_day_cleaning_substitute_accepted: null,
      customer_reported_hk_no_showed: null,
      unassigned_notification_sent: null,
      cleaning_completed_without_hk_confirmation: null,
      hk_completed_cleaning: 'assets/img/map-dark-green-check.svg',
      hk_checked_in: 'assets/img/map-black-check.svg',
      concierge_reported_hk_no_showed: null,
      manual_substitute_requested: null,
      manual_substitute_accepted: null,
    };
    return momentStyleArray[momentType] || 'assets/img/map-black-check.svg';
  }

  getJobBookingFormAnswers(bookingId) {
    const url = `api/v1/customer/customer-booking-form-answers?booking_id=${bookingId}`;
    return this.httpClient.get(url);
  }

  getProRequestBookingFormAnswers(availabilityRequestId) {
    const url = `api/v1/customer/customer-booking-form-answers?filters[availability_request_ids]=${availabilityRequestId}`;
    return this.httpClient.get(url);
  }

  updateTableSearch(properties: Properties, search: string): Properties {
    const lowerCaseSearch = search.toLowerCase();
    // If no search string, just return properties with their existing filtered arrays
    if (!search) {
      return properties;
    }
    return properties.filter((property) => {
      const matchInProperty = property?.name?.toLowerCase()?.includes(lowerCaseSearch) ||
        property?.address?.toLowerCase()?.includes(lowerCaseSearch);
      // Search within already filtered jobs (not all jobs)
      const matchInJobs = property?.filteredJobs?.some((job) => {
        const jobMatch = job?.serviceCategory?.toLowerCase()?.includes(lowerCaseSearch) ||
          job?.status?.name?.toLowerCase()?.includes(lowerCaseSearch) ||
          job?.homekeeperNames?.toLowerCase()?.includes(lowerCaseSearch);
        return jobMatch;
      });
      // Search within already filtered reservations
      const matchInReservations = property?.filteredReservations?.some((reservation) => {
        const reservationMatch = reservation?.title?.toLowerCase()?.includes(lowerCaseSearch);
        return reservationMatch;
      });
      // Search within already filtered availability requests
      const matchInAvailabilityRequests = property?.filteredAvailabilityRequests?.some((request) => {
        const requestMatch = request?.serviceCategory?.toLowerCase()?.includes(lowerCaseSearch) ||
          request?.title?.toLowerCase()?.includes(lowerCaseSearch);
        return requestMatch;
      });
      return matchInProperty || matchInJobs || matchInReservations || matchInAvailabilityRequests;
    }).map((property) => {
      // Apply search filter on top of existing filters (use filteredJobs not jobs)
      const originalFilteredJobs = property.filteredJobs ?? [];
      const originalFilteredReservations = property.filteredReservations ?? [];
      const originalFilteredAvailabilityRequests = property.filteredAvailabilityRequests ?? [];
      // Check if search matches property name/address
      const matchesProperty = property?.name?.toLowerCase()?.includes(lowerCaseSearch) ||
        property?.address?.toLowerCase()?.includes(lowerCaseSearch);
      // If search matches the property name/address, show all filtered items for that property
      if (matchesProperty) {
        return {
          ...property,
          filteredJobs: originalFilteredJobs,
          filteredReservations: originalFilteredReservations,
          filteredAvailabilityRequests: originalFilteredAvailabilityRequests
        };
      }
      // Otherwise, filter jobs/reservations/requests by the search term
      let searchFilteredJobs = originalFilteredJobs.filter((job) => job?.serviceCategory?.toLowerCase()?.includes(lowerCaseSearch) ||
          job?.status?.name?.toLowerCase()?.includes(lowerCaseSearch) ||
          job?.homekeeperNames?.toLowerCase()?.includes(lowerCaseSearch));
      let searchFilteredReservations = originalFilteredReservations.filter((reservation) => reservation?.title?.toLowerCase()?.includes(lowerCaseSearch));
      let searchFilteredAvailabilityRequests = originalFilteredAvailabilityRequests.filter((request) => request?.serviceCategory?.toLowerCase()?.includes(lowerCaseSearch) ||
          request?.title?.toLowerCase()?.includes(lowerCaseSearch));
      // If search doesn't match any jobs but matches property name, show all filtered jobs
      if (searchFilteredJobs.length === 0 && searchFilteredAvailabilityRequests.length === 0) {
        searchFilteredJobs = originalFilteredJobs;
        searchFilteredAvailabilityRequests = originalFilteredAvailabilityRequests;
        searchFilteredReservations = searchFilteredReservations.length > 0 ? searchFilteredReservations : originalFilteredReservations;
      }
      return {
        ...property,
        filteredJobs: searchFilteredJobs,
        filteredReservations: searchFilteredReservations,
        filteredAvailabilityRequests: searchFilteredAvailabilityRequests
      };
    });
  }

  updateTableFilter(properties: Properties, filters: SelectedFilters): Properties {
    const { status, type, service, address, propertyName } = filters;
    const filteredProperties = properties.filter((property) => {
      if (Object.keys(filters).every(key => filters[key].length === 0)) {
        property.filteredJobs = property.jobs;
        property.filteredAvailabilityRequests = property.availabilityRequests;
        property.filteredReservations = property.reservations;
        return true;
      }
      // Initialize filtered arrays
      let filteredJobs = property.jobs;
      let filteredAvailabilityRequests = property.availabilityRequests;
      let filteredReservations = property.reservations;

      // Status filter
      if (status.length > 0) {
        if (status.includes('request_waiting_reply')) {
          filteredAvailabilityRequests = property.availabilityRequests;
        } else {
          filteredAvailabilityRequests = [];
        }

        filteredJobs = filteredJobs.filter((job) => {
          if (job?.status?.status === 'cancelled') {
            return status.includes('client_cancelled') || status.includes('request_cancelled');
          } else if (job?.status?.status === 'scheduled') {
            return status.includes('scheduled') || status.includes('checked_in');
          } else {
            return status.includes(job?.status?.status);
          }
        });
      }

      // Type filter
      if (type.length > 0) {
        if (!type.includes('reservation')) {
          filteredReservations = [];
        }
        if (!type.includes('job')) {
          filteredJobs = [];
        }
      }

      // Service filter
      if (service.length > 0) {
        filteredJobs = filteredJobs.filter((job) =>
          service.includes(job?.serviceCategory)
        );
      }

      // Set the filtered results back to the property
      property.filteredJobs = filteredJobs;
      property.filteredAvailabilityRequests = filteredAvailabilityRequests;
      property.filteredReservations = filteredReservations;

      if ((service.length > 0 || status.length > 0) && !type.includes('reservation')) {
        return (filteredJobs.length > 0 || filteredAvailabilityRequests.length > 0);
      }

      if (address.includes(property.id) || propertyName.includes(property.id)) {
        return true;
      }

      // Return true if any of the filtered arrays have items and address and property name match
      return (filteredJobs.length > 0 || filteredAvailabilityRequests.length > 0 || filteredReservations.length > 0);
    });
    return filteredProperties;
  }

  addSkeletonJobToCalendar(jobDate, jobTime, addressId) {
    localStorage.setItem('skipSortingProperties', 'true');
    this.properties.map((property) => {
      if (property.id === addressId) {
        let foundIndex = 0;
        property.jobs.map((job, index) => {
          const time = LuxonDateTime.fromFormat(job.time, 'h:mma');
          const formJobTime = LuxonDateTime.fromFormat(jobTime, 'H:mm');
          if (time < formJobTime) {
            foundIndex = index + 1;
          }
        });
        const luxonJobDate = LuxonDateTime.fromISO(jobDate)
        const startWeekday = luxonJobDate.toFormat('cccc');
        property.jobs.splice(foundIndex, 0, {
          startDate: luxonJobDate.toISODate(),
          endDate: '',
          skeleton: true,
          startWeekday,
          homekeeperName: '',
          id: 0,
          title: '',
          endWeekday: '',
          time: '',
          status: '',
          isPrivate: true,
          isJobRequest: false,
          serviceCategory: '',
          period: {
            startDate: '',
            endDate: '',
          },
          booking: {
            startDateNoEarlierThan: '',
            startTimeNoEarlierThan: '',
            endDateNoLaterThan: '',
            endTimeNoLaterThan: '',
            id: 0
          },
          icon: '',
        });
      }
    });
  }

  removeSkeletonJobFromCalendar() {
    this.properties.forEach((property) => {
      property.jobs = property.jobs.filter(job => !job.skeleton);
      property.filteredJobs = property.filteredJobs.filter(job => !job.skeleton);
    });
  }

  getFilteredWaterfallHistory(history) {
    const dontIncludeList = [
      'Starting to notify pros',
      'All Pros on your priority',
      'We are starting to contact',
      'No pros found'
    ];
    const mergedDeclinedHkIds = new Set();
    history.forEach((notification) => {
      if (notification.data.type === 'declined') {
        history.forEach((item) => {
          if (item.data.type !== 'declined' && item.data.homekeeper_id === notification.data.homekeeper_id) {
            item.data.type = 'declined';
            item.data['declined_show_at'] = notification.data.show_at;
            item.data['declined_reason'] = notification.text;
            mergedDeclinedHkIds.add(notification.data.homekeeper_id);
          }
        });
      }
    });

    history = history.filter((notification) => {
      if (notification.data.type === 'declined' && notification.text?.includes('declined') && mergedDeclinedHkIds.has(notification.data.homekeeper_id)) {
        return false;
      }
      return !dontIncludeList.some(item => notification.text?.includes(item));
    });

    history.map((notification) => {
      if (notification.text === 'Searching via Tidy Find New Pro' || notification.text === 'Contacting new pros' || notification.text === 'No pros found') {
        notification.text = 'New Pros';
      } else if (notification.text?.includes('declined')) {
        if (!notification.data.declined_reason) {
          notification.data.declined_reason = notification.text;
        }
        notification.text = notification.text.replace(/\s+declined.*$/, '');
      } else if (notification.text?.includes('contacted')) {
        notification.text = notification.text.replace(/\s+contacted$/, '');
      } else if (notification.text?.includes('accepted')) {
        notification.text = notification.text.replace(/\s+accepted$/, '');
      }
      if (!notification.data.happened) {
        const contactTime = parseFloat(notification.data.hours_until_next_contact);
        if (notification.data.hours_until_next_contact === 0) {
          notification['descriptionText'] = 'contacting';
        } else if (notification.data.hours_until_next_contact) {
          if (contactTime < 24) {
            const totalMinutes = Math.round(contactTime * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            let timeText = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : '';
            timeText += minutes > 0 ? `${timeText ? ' ' : ''}${minutes} minute${minutes > 1 ? 's' : ''}` : '';
            notification['descriptionText'] = `contacting in ${timeText}`;
          } else {
            const contactDateTime = LuxonDateTime.now().plus({ hours: contactTime });
            notification['descriptionText'] = `contacting on ${new ParseDateTimePipe().transform(contactDateTime.toISO(), 'ddd MMM d')} ${new ParseDateTimePipe().transform(contactDateTime.toISO(), 'h:mma')}`;
          }
        }
      }
    });
    return history;
  }

  async getDefaultScheduleViewMode() {
    const addressResponse = await this.client.getMoreDetailAddresses();
    return addressResponse.length > 3 || !this.windowService.isDesktopRes ? 'WEEKLY' : 'MONTHLY';
  }

  parseCalendarJobs(jobs: any[], address: AddressModel): Job[] {
    const parsedJobs = jobs.map((job) => {
      let parsedStartDate = LuxonDateTime.fromFormat(job?.booking?.start_date_no_earlier_than, 'MM/dd/yyyy').toISODate();
      let parsedEndDate = LuxonDateTime.fromFormat(job?.booking?.end_date_no_later_than, 'MM/dd/yyyy').toISODate();
      if (!job?.is_job_request) {
        parsedStartDate = LuxonDateTime.fromFormat(job?.date, 'MMM d, yyyy').toISODate();
        parsedEndDate = LuxonDateTime.fromFormat(job?.date, 'MMM d, yyyy').toISODate();
      }
      const startWeekday = this.util.getWeekdayFromDay(parsedStartDate);
      const endWeekday = this.util.getWeekdayFromDay(parsedEndDate);
      const timezone = job.timezone
      const dateTime = DateTime.fromFormat(job.start_time, 'HH:mm', { zone: timezone }).setLocale('en-US');
      const formattedTime = (dateTime.toLocaleString(DateTime.TIME_SIMPLE)).replace(' AM', 'am').replace(' PM', 'pm')
      const jobIcon = this.getJobIcon(job?.is_job_request, job?.service_icon_url, job?.job_progress)
      const homekeeperNames = job?.homekeeper_jobs?.map((homekeeper) => homekeeper?.homekeeper?.name).join(', ');
      const status = this.reportIssue.getJobStatusData(job?.homekeeper_jobs, job?.job_progress, job);
      const jobDate = LuxonDateTime.fromISO(parsedStartDate);
      const monthName = jobDate.toFormat('MMMM');
      const translatedMonth = new TranslationPipe().transform(monthName);
      const truncatedMonth = translatedMonth.slice(0, 3);
      const dayNumber = parseInt(jobDate.toFormat('d')); // Remove leading zero
      const formattedDate = `${truncatedMonth} ${dayNumber}, ${jobDate.toFormat('yyyy')}`;

      // Convert startDateNoEarlierThan to ISO format
      let isoStartDateNoEarlierThan = job?.booking?.start_date_no_earlier_than;
      if (isoStartDateNoEarlierThan) {
        isoStartDateNoEarlierThan = LuxonDateTime.fromFormat(isoStartDateNoEarlierThan, 'MM/dd/yyyy').toISO();
      }

      // Convert endDateNoLaterThan to ISO format
      let isoEndDateNoLaterThan = job?.booking?.end_date_no_later_than;
      if (isoEndDateNoLaterThan) {
        isoEndDateNoLaterThan = LuxonDateTime.fromFormat(isoEndDateNoLaterThan, 'MM/dd/yyyy').toISO();
      }

      return {
        ...job,
        id: job.id,
        address,
        title: homekeeperNames ? homekeeperNames : job?.service_name,
        startWeekday,
        homekeeperName: job?.potential_homekeeper?.name || job?.homekeeper_jobs[0]?.homekeeper?.name.split(' ').map(name => name[0]).join(''),
        homekeeperNames,
        startDate: parsedStartDate,
        endWeekday,
        endDate: parsedEndDate,
        time: formattedTime,
        icon: jobIcon,
        status,
        isPrivate: job?.is_private,
        isJobRequest: job?.is_job_request,
        serviceCategory: job?.service_type_details?.name || job?.service_type_details?.service_category?.name || job?.service_category,
        period: {
          startDate: parsedStartDate,
          endDate: parsedEndDate,
        },
        booking: {
          startDateNoEarlierThan: isoStartDateNoEarlierThan,
          startTimeNoEarlierThan: job?.booking?.start_time_no_earlier_than,
          endDateNoLaterThan: isoEndDateNoLaterThan,
          endTimeNoLaterThan: job?.booking?.end_time_no_later_than,
          id: job?.booking?.id
        },
        object: job,
        date: formattedDate // Use the new formatted date
      }
    })
    return parsedJobs;
  }

  parseCalendarReservations(reservations: any[], address: AddressModel, selectedPeriodRange: any, daysOfWeek: string[]): Reservation[] {
    const parsedReservations = reservations.map((reservation) => {
      const parsedStartDate = LuxonDateTime.fromFormat(reservation?.check_in_date, 'MMM d, yyyy').toISODate();
      const parsedEndDate = LuxonDateTime.fromFormat(reservation?.check_out_date, 'MMM d, yyyy').toISODate();
      const isStartDateInsidePeriod = this.util.isDateInsidePeriod(parsedStartDate, selectedPeriodRange);
      const isEndDateInsidePeriod = this.util.isDateInsidePeriod(parsedEndDate, selectedPeriodRange);
      const startWeekday = isStartDateInsidePeriod ? this.util.getWeekdayFromDay(parsedStartDate) : daysOfWeek[0];
      const endWeekday = isEndDateInsidePeriod ? this.util.getWeekdayFromDay(parsedEndDate) : daysOfWeek[6];
      // Inquiries have no firm check-in/out times (reservations do). Guard the
      // parse: DateTime.fromFormat(undefined, …) throws, which would abort the
      // whole map and drop the row (and any reservations parsed alongside it).
      const checkInDateTime = reservation?.check_in_time
        ? DateTime.fromFormat(reservation.check_in_time, 'HH:mm', { zone: reservation.timezone }).setLocale('en-US')
        : null;
      const checkOutDateTime = reservation?.check_out_time
        ? DateTime.fromFormat(reservation.check_out_time, 'HH:mm', { zone: reservation.timezone }).setLocale('en-US')
        : null;
      const formattedCheckInTime = checkInDateTime
        ? checkInDateTime.toLocaleString(DateTime.TIME_SIMPLE).replace(' AM', 'am').replace(' PM', 'pm')
        : '';
      const formattedCheckOutTime = checkOutDateTime
        ? checkOutDateTime.toLocaleString(DateTime.TIME_SIMPLE).replace(' AM', 'am').replace(' PM', 'pm')
        : '';
      const computedNights = parsedStartDate && parsedEndDate
        ? Math.max(0, Math.round(LuxonDateTime.fromISO(parsedEndDate).diff(LuxonDateTime.fromISO(parsedStartDate), 'days').days))
        : undefined;
      const src = reservation?.source_data || {};
      const srcGuest = src?.guest || {};
      const guestFirstName = srcGuest.first_name || reservation?.guest_first_name || undefined;
      const guestLastName = srcGuest.last_name || reservation?.guest_last_name || undefined;
      const guestName = reservation?.guest_name
        || [guestFirstName, guestLastName].filter(Boolean).join(' ').trim()
        || undefined;
      const guests: ReservationGuest[] = (reservation?.guests || []).map((guest) => ({
        id: guest.id,
        name: guest.name,
        email: guest.email,
        phone: guest.phone,
        platform: guest.platform,
        isBooker: guest.is_booker,
        isStaying: guest.is_staying,
        chatRoomKey: guest.chat_room_key || guest.chatRoomKey || null,
      }));
      const primaryGuest = guests.find((guest) => guest.isBooker)
        ?? guests.find((guest) => guest.isStaying)
        ?? guests[0];
      const centsToDollars = (cents: number | undefined | null): number | undefined =>
        cents === null || cents === undefined ? undefined : Number(cents) / 100;
      const total = centsToDollars(reservationPriceCents(reservation))
        ?? src?.total_amount
        ?? reservation?.total
        ?? reservation?.total_amount;
      const paid = centsToDollars(reservation?.paid_amount)
        ?? src?.amount_paid
        ?? reservation?.paid
        ?? reservation?.total_paid;
      const explicitOwes = centsToDollars(reservation?.amount_due)
        ?? src?.amount_due
        ?? reservation?.owes
        ?? reservation?.total_owes;
      const owes = explicitOwes ?? (
        total !== undefined && paid !== undefined ? Math.max(0, total - paid) : undefined
      );
      const currency = reservationCurrency(reservation)
        || src?.currency_code
        || src?.currency
        || 'USD';
      const sourceName = normalizeSourceName(
        reservation?.source_type_name
          || reservation?.guest_reservation_source_type?.name
          || src?.platform
      );
      return {
        ...reservation,
        id: reservation.id,
        address,
        title: `${new ParseDateTimePipe().transform(parsedStartDate, 'MMM d')  } - ${  new ParseDateTimePipe().transform(parsedEndDate, 'MMM d')}`,
        startWeekday,
        startDate: isStartDateInsidePeriod ? parsedStartDate : selectedPeriodRange.startDate,
        endWeekday,
        endDate: isEndDateInsidePeriod ? parsedEndDate : selectedPeriodRange.endDate,
        icon: 'assets/img/calendar-link.svg',
        period: {
          startDate: parsedStartDate,
          endDate: parsedEndDate,
        },
        object: reservation,
        checkInTime: formattedCheckInTime,
        checkOutTime: formattedCheckOutTime,
        guestName: primaryGuest?.name || guestName,
        guestFirstName,
        guestLastName,
        guests,
        guestProfilePicture: srcGuest.profile_picture || undefined,
        totalGuests: reservation?.total_guests,
        totalAdults: reservation?.total_adults,
        totalChildren: reservation?.total_children,
        totalInfants: reservation?.total_infants,
        totalPets: reservation?.total_pets,
        nights: reservation?.nights ?? src?.nights ?? computedNights,
        total,
        paid,
        owes,
        currency,
        confirmationCode: reservation?.confirmation_code || src?.code,
        sourceName,
        sourceLogoUrl: reservation?.guest_reservation_source_type?.logo_url
          || reservation?.source_logo_url,
        platform: src?.platform,
        bookingDate: src?.booking_date,
        conversationId: src?.conversation_id,
        notes: src?.notes || reservation?.notes,
        guidebookUrl: reservation?.guidebook_url,
        phone: primaryGuest?.phone || srcGuest.phone_numbers || reservation?.guest_phone || reservation?.phone,
        guestEmail: primaryGuest?.email || srcGuest.email || reservation?.guest_email || reservation?.email,
        messageText: reservation?.message_text || reservation?.guest_message,
        source: reservation?.source,
        totalAmount: reservation?.total_amount ?? reservation?.total,
        pricingBreakdown: reservation?.pricing_breakdown
          ? {
              nightlyRate: reservation.pricing_breakdown.nightly_rate,
              nights: reservation.pricing_breakdown.nights,
              cleaningFee: reservation.pricing_breakdown.cleaning_fee,
              serviceFee: reservation.pricing_breakdown.service_fee,
              taxes: reservation.pricing_breakdown.taxes,
              discounts: reservation.pricing_breakdown.discounts,
            }
          : undefined,
        channelBreakdown: channelBreakdownView(src, sourceName, currency),
        state: this.reservationState(reservation),
        stateColor: reservation?.state_color,
        stateIcon: reservation?.state_icon,
        quoteAmount: reservation?.quote_amount,
        quoteSentAt: reservation?.quote_sent_at,
        quoteValidUntil: reservation?.quote_valid_until,
        quoteViewedAt: reservation?.quote_viewed_at,
        holdType: reservation?.hold_type,
        holdExpiresAt: reservation?.hold_expires_at,
        depositAmount: reservation?.deposit_amount,
        depositStatus: reservation?.deposit_status,
        firstResponseAt: reservation?.first_response_at,
        followUpCount: reservation?.follow_up_count,
        assignedToId: reservation?.assigned_to_id,
        reviewStatus: reservation?.review_status,
      }
    })
    const removedDuplicateds = parsedReservations.filter((reservation, index, self) =>
      index === self.findIndex((t) => (
        t.startDate === reservation.startDate && t.endDate === reservation.endDate && t.id === reservation.id
      ))
    )
    return removedDuplicateds;
  }

  // Inquiries come from the calendar-view's own `inquiries` layer and are
  // tagged `type: 'inquiry'` by the caller before parsing; the schedule UI
  // keys inquiry cards/banners off state === 'inquiry'. A tentative
  // reservation is a Vrbo hold the guest hasn't confirmed yet; it drops back
  // to a regular stay once the API reports `is_tentative: false`.
  private reservationState(reservation: any): ReservationState | undefined {
    if (reservation?.state) {return reservation.state;}
    if (reservation?.type === 'inquiry') {return 'inquiry';}
    if (reservation?.is_tentative) {return 'hold';}
    return undefined;
  }

  parseCalendarAvailabilityRequests(requests: any[], address: AddressModel, _selectedPeriodRange: any, _daysOfWeek: string[]): AvailabilityRequest[] {
    const requestsWithoutDuplicates = requests.filter((request, index, self) =>
      index === self.findIndex((t) => (
        t.id === request.id
      ))
    )
    const parsedRequests = requestsWithoutDuplicates.map((request) => {
      const firstRequestDate = request?.request_times?.find((requestTime) => {
        const today = LuxonDateTime.local().toJSDate().setHours(0, 0, 0, 0);
        const parsedDate = LuxonDateTime.fromISO(requestTime?.date).toJSDate().setHours(0, 0, 0, 0);
        return parsedDate >= today;
      })?.date;
      const parsedStartDate = LuxonDateTime.fromISO(firstRequestDate).toISODate();
      const parsedEndDate = parsedStartDate;
      const startWeekday = this.util.getWeekdayFromDay(parsedStartDate);
      const endWeekday = this.util.getWeekdayFromDay(parsedEndDate);
      const jobIcon = this.getJobIcon(true, request?.service_type_details?.service_category?.icon_url, request?.status)
      const status = this.reportIssue.getJobStatusData(null, request?.job_progress);
      const homekeeperName = request?.homekeeper?.name;
      let parsedRequestTimes = '';
      request?.request_times.map((time, index) => {
        const startTime = LuxonDateTime.fromFormat(time.requested_times[0], 'HH:mm').toFormat('h:mma').toLowerCase();
        const endTime = LuxonDateTime.fromFormat(time.requested_times[time.requested_times.length - 1], 'HH:mm').toFormat('h:mma').toLowerCase();
        parsedRequestTimes += `${LuxonDateTime.fromISO(time?.date).toFormat('MMM d')  }<br>${  startTime  } - ${  endTime}`;
        if (index !== request?.request_times.length - 1) {
          parsedRequestTimes += '<br><br>';
        }
      });
      const initiatedBy = request?.initiated_by;
      return {
        id: request?.id,
        address,
        title: homekeeperName ? homekeeperName : request?.service_type_details?.name,
        startWeekday,
        startDate: parsedStartDate,
        endWeekday,
        endDate: parsedEndDate,
        icon: jobIcon,
        status,
        isPrivate: true,
        isJobRequest: true,
        serviceCategory: request?.service_type_details?.name,
        period: {
          startDate: parsedStartDate,
          endDate: parsedEndDate,
        },
        parsedRequestTimes,
        initiatedBy,
        object: request
      }
    })
    return parsedRequests;
  }

  getJobIcon(isJobRequest: boolean, serviceIconUrl: string, _jobProgress: string): string {
    return (serviceIconUrl || 'assets/img/mop.png');
    /*const warningStatuses = ['request_failed', 'client_cancelled', 'homekeeper_cancelled', 'having_issues'];
    if (isJobRequest) {
      if (jobProgress === 'request_waiting_reply' || jobProgress === 'pending') {
        return 'assets/icon/searching.svg';
      }
    }
    return warningStatuses?.includes(jobProgress) ? 'assets/img/warning.png' : (serviceIconUrl || 'assets/img/mop.png');*/
  }

  setReloadSchedulePage() {
    this.reloadSchedulePage$.next(true);
  }

  getReloadSchedulePage() {
    return this.reloadSchedulePage$.asObservable();
  }

  getBooking(bookingId) {
    const url = `api/v1/customer/booking-details/${bookingId}?includes=booking_notes,custom_fields,address,activity_timelines`;
    return this.httpClient.get(url);
  }

}

export interface CalendarViewJobResponse {
  id?: number | null;
  date?: string | null;
  address_id?: number | null;
  service_name?: string | null;
  service_category?: string | null;
  service_icon_url?: string | null;
  service_map_icon_url?: string | null;
  start_time?: string | null;
  timezone?: string | null;
  is_private?: boolean | null;
  job_progress?: string | null;
  chat_rooms_with_unread_messages?: any[] | null;
  is_job_request?: boolean | null;
  booking?: {
    id?: number | null;
    start_date_no_earlier_than?: string | null;
    start_time_no_earlier_than?: string | null;
    end_date_no_later_than?: string | null;
    end_time_no_later_than?: string | null;
  } | null;
  homekeeper_jobs?: {
    id?: number | null;
    homekeeper?: {
      id?: number | null;
      name?: string | null;
    } | null;
  }[] | null;
  [key: string]: any;
}
